/**
 * BGM 模块 - Web Audio 实时合成的 chiptune
 * 风格：8-bit 像素游戏风，诡谲 + 稍微欢快
 *   - Square wave 主导旋律（带 detune 增加失真感）
 *   - Triangle wave 弹贝斯
 *   - Noise burst 做 hat
 *   - 132 BPM 偏明快节奏
 *   - A 小调为主，加 tritone 增"诡"感
 *
 * 必须在用户点击后启动（浏览器自动播放策略）
 */

type OscType = "square" | "triangle" | "sawtooth" | "sine";

// === 音符定义（MIDI 编号） ===
// C2=36, C3=48, C4=60, C5=72, A4=69 等
// 0 = 休止

// 旋律通道：4 小节 × 4 个 8 分音符 = 16 步
// 偏诡谲：含小二度、增四度、半音下行
const LEAD_PATTERN: (number | null)[] = [
  // Bar 1: A4 - E5 A4  (A 小调主旋律)
  69, 76, 0, 81,
  // Bar 2: G5 F5 E5 D5  (下行四音列)
  79, 77, 76, 74,
  // Bar 3: C5 E5 - G5  (跳进)
  72, 76, 0, 79,
  // Bar 4: F5 D5 E5 A4  (回到主音)
  77, 74, 76, 69,
];

// 第二段旋律（4 小节之后循环回来时略有变化）
const LEAD_PATTERN_B: (number | null)[] = [
  // Bar 1: A4 E5 G5 A5 (上升)
  69, 76, 79, 81,
  // Bar 2: B5 A5 G5 F5 (高区下行)
  83, 81, 79, 77,
  // Bar 3: E5 G5 - E5 (跳进)
  76, 79, 0, 76,
  // Bar 4: D5 F5 A4 A4 (解决)
  74, 77, 69, 69,
];

// 贝斯通道：每小节 2 个根音（半拍节奏）
const BASS_PATTERN: (number | null)[] = [
  // Bar 1: A2 A2
  45, 45,
  // Bar 2: G2 G2 (借用)
  43, 43,
  // Bar 3: A2 A2
  45, 45,
  // Bar 4: A2 A2 (回到主)
  45, 45,
];

// 鼓点 - Kick：每小节 1 和 3 拍
const KICK_PATTERN: boolean[] = [
  true, false, true, false,    // bar 1
  true, false, true, false,    // bar 2
  true, false, true, false,    // bar 3
  true, false, true, false,    // bar 4
];

// 鼓点 - Hat：每 8 分音符
const HAT_PATTERN: boolean[] = [
  false, true, false, true,     // bar 1
  false, true, false, true,     // bar 2
  false, true, false, true,     // bar 3
  false, true, false, true,     // bar 4
];

const STEPS_PER_BAR = 4;
const BARS = 4;
const STEPS_PER_LOOP = STEPS_PER_BAR * BARS;
const STEP_DURATION = 60 / 132 / 2; // 8th note at 132 BPM, in seconds

function midiToFreq(midi: number): number {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

export class Chiptune {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private isPlaying = false;
  private nextStepTime = 0;
  private stepIndex = 0;
  private loopCount = 0;
  private timer: number | null = null;
  private currentVolume = 0.25;
  private onStateChange: ((playing: boolean) => void) | null = null;

  setStateListener(fn: (playing: boolean) => void) {
    this.onStateChange = fn;
  }

  isReady(): boolean {
    return this.ctx !== null;
  }

  /**
   * 必须在用户点击事件中调用
   */
  async start(): Promise<void> {
    if (this.isPlaying) return;
    if (!this.ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.currentVolume;
      this.masterGain.connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") {
      await this.ctx.resume();
    }
    this.isPlaying = true;
    this.stepIndex = 0;
    this.loopCount = 0;
    this.nextStepTime = this.ctx.currentTime + 0.05;
    this.scheduler();
    this.onStateChange?.(true);
  }

  stop(): void {
    if (!this.isPlaying) return;
    this.isPlaying = false;
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
    this.onStateChange?.(false);
  }

  toggle(): boolean {
    if (this.isPlaying) {
      this.stop();
      return false;
    } else {
      this.start();
      return true;
    }
  }

  setVolume(v: number): void {
    this.currentVolume = Math.max(0, Math.min(1, v));
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.setTargetAtTime(this.currentVolume, this.ctx.currentTime, 0.02);
    }
  }

  getVolume(): number {
    return this.currentVolume;
  }

  getPlaying(): boolean {
    return this.isPlaying;
  }

  private scheduler = (): void => {
    if (!this.isPlaying || !this.ctx) return;
    const horizon = this.ctx.currentTime + 0.1;
    while (this.nextStepTime < horizon) {
      this.playStep(this.stepIndex, this.nextStepTime);
      this.nextStepTime += STEP_DURATION;
      this.stepIndex++;
      if (this.stepIndex >= STEPS_PER_LOOP) {
        this.stepIndex = 0;
        this.loopCount++;
      }
    }
    if (this.isPlaying) {
      this.timer = window.setTimeout(this.scheduler, 25);
    }
  };

  private playStep(step: number, time: number): void {
    if (!this.ctx || !this.masterGain) return;
    const leadPattern = this.loopCount % 2 === 0 ? LEAD_PATTERN : LEAD_PATTERN_B;
    const leadNote = leadPattern[step];
    const bassNote = BASS_PATTERN[step];
    const kick = KICK_PATTERN[step];
    const hat = HAT_PATTERN[step];

    if (leadNote != null && leadNote > 0) {
      this.playLead(leadNote, time);
    }
    if (bassNote != null && bassNote > 0) {
      this.playBass(bassNote, time);
    }
    if (kick) {
      this.playKick(time);
    }
    if (hat) {
      this.playHat(time);
    }
  }

  /**
   * 主导旋律：Square wave + 轻微 detune（增加失真感）
   */
  private playLead(midi: number, time: number): void {
    if (!this.ctx || !this.masterGain) return;
    const freq = midiToFreq(midi);

    // 主振
    const osc1 = this.ctx.createOscillator();
    osc1.type = "square";
    osc1.frequency.setValueAtTime(freq, time);

    // 微微 detune 的副振（营造失真/诡谲）
    const osc2 = this.ctx.createOscillator();
    osc2.type = "square";
    osc2.frequency.setValueAtTime(freq * 1.005, time); // 5 cent detune
    osc2.detune.setValueAtTime(-7, time);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.18, time + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, time + STEP_DURATION * 0.9);

    // 低通滤波（模拟 8-bit 频段限制）
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(3500, time);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(filter);
    filter.connect(this.masterGain);

    osc1.start(time);
    osc2.start(time);
    osc1.stop(time + STEP_DURATION);
    osc2.stop(time + STEP_DURATION);
  }

  /**
   * 贝斯：Triangle wave，厚实
   */
  private playBass(midi: number, time: number): void {
    if (!this.ctx || !this.masterGain) return;
    const freq = midiToFreq(midi);

    const osc = this.ctx.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(freq, time);

    // 加一个八度叠
    const osc2 = this.ctx.createOscillator();
    osc2.type = "triangle";
    osc2.frequency.setValueAtTime(freq * 2, time);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.22, time + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.001, time + STEP_DURATION * 1.8);

    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(800, time);

    osc.connect(gain);
    osc2.connect(gain);
    gain.connect(filter);
    filter.connect(this.masterGain);

    osc.start(time);
    osc2.start(time);
    osc.stop(time + STEP_DURATION * 2);
    osc2.stop(time + STEP_DURATION * 2);
  }

  /**
   * Kick：正弦波快速降频
   */
  private playKick(time: number): void {
    if (!this.ctx || !this.masterGain) return;
    const osc = this.ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(150, time);
    osc.frequency.exponentialRampToValueAtTime(40, time + 0.12);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.4, time + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.18);

    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(time);
    osc.stop(time + 0.2);
  }

  /**
   * Hat：白噪短促
   */
  private playHat(time: number): void {
    if (!this.ctx || !this.masterGain) return;
    // 用 noise buffer 制造白噪
    const bufferSize = this.ctx.sampleRate * 0.05;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const source = this.ctx.createBufferSource();
    source.buffer = buffer;

    const filter = this.ctx.createBiquadFilter();
    filter.type = "highpass";
    filter.frequency.setValueAtTime(7000, time);

    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, time);
    gain.gain.linearRampToValueAtTime(0.06, time + 0.002);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.04);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain);
    source.start(time);
    source.stop(time + 0.05);
  }
}

let _instance: Chiptune | null = null;
export function getBgm(): Chiptune {
  if (!_instance) _instance = new Chiptune();
  return _instance;
}
